import React, { forwardRef, memo } from 'react';
import classNames from 'classnames';
import mergeCssModules from '@lib/utils/mergeCssModules';
import selectStyles from '@lib/utils/selectStyles';
import Loader from '@lib/components/CircleLoader/CircleLoader';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import defaultStyles from './Default.module.scss';
import themeStyles from './IconButton.module.scss';

export type LoaderProps = { [key: string]: string | number };

export enum IconButtonTypes {
  PrimaryOutlined = 'primary-outlined',
  PrimaryFilled = 'primary-filled',
  SecondaryOutlined = 'secondary-outlined',
  SecondaryFilled = 'secondary-filled',
  SecondaryInvert = 'secondary-invert',
  TertiaryFilled = 'tertiary-filled',
  TertiaryInvert = 'tertiary-invert',
  QuaternaryOutlined = 'quaternary-outlined',
  QuaternaryFilled = 'quaternary-filled',
  NegativeOutlined = 'negative-outlined',
  NegativeFilled = 'negative-filled',
  WarningOutlined = 'warning-outlined',
  WarningFilled = 'warning-filled',
}

export enum IconButtonSizes {
  Medium = 'medium',
  Small = 'small',
  ExtraSmall = 'extraSmall',
}

export interface IconButtonProps {
  'aria-label'?: string;
  classes?: Record<string, string>;
  className?: string;
  counter?: number;
  'data-test-id'?: string;
  disabled?: boolean;
  filled?: boolean;
  icon?: string;
  iconSize?: number;
  isFocused?: boolean;
  isLoading?: boolean;
  loaderProps?: LoaderProps;
  navigate?: (to: string) => void;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  rounded?: boolean;
  size?: IconButtonSizes;
  svgIcon?: React.ReactNode;
  symbolsOutlined?: boolean;
  target?: string;
  to?: string;
  type?: IconButtonTypes;
  useRipple?: boolean;
}

const createRipple = (event: React.MouseEvent<HTMLElement>) => {
  const button = event.currentTarget;
  const diameter = button.clientWidth;
  const circle = document.createElement('span');
  circle.style.width = `${diameter}px`;
  circle.style.height = `${diameter}px`;
  circle.style.left = '0px';
  circle.style.top = '0px';
  circle.classList.add(defaultStyles.ripple);
  const ripple = button.getElementsByClassName(defaultStyles.ripple)[0];
  if (ripple && ripple.parentElement !== null) {
    ripple.parentElement.removeChild(ripple);
  }
  button.appendChild(circle);
};

const IconButton = forwardRef(
  (props: IconButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    const {
      'aria-label': ariaLabel,
      classes,
      className,
      counter,
      'data-test-id': dataTestId,
      disabled = false,
      filled,
      icon,
      iconSize,
      isFocused,
      isLoading,
      loaderProps,
      navigate,
      onClick,
      rounded,
      size = IconButtonSizes.Medium,
      svgIcon,
      symbolsOutlined,
      target,
      to,
      type = IconButtonTypes.PrimaryOutlined,
      useRipple = true,
    } = props;

    let iconSizeValue = iconSize;
    if (!iconSizeValue && size === IconButtonSizes.Medium) {
      iconSizeValue = 24;
    } else if (!iconSizeValue && size === IconButtonSizes.Small) {
      iconSizeValue = 16;
    } else if (!iconSizeValue && size === IconButtonSizes.ExtraSmall) {
      iconSizeValue = 16;
    }

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (useRipple) {
        createRipple(event);
      }
      if (to && target === '_blank') {
        window.open(to, '_blank')?.focus();
      } else if (to && navigate) {
        navigate(to);
      } else if (onClick) {
        onClick(event);
      }
    };

    const onMouseUp = (event: React.MouseEvent<HTMLElement>) =>
      event.currentTarget.blur();

    const styles = mergeCssModules(
      defaultStyles,
      selectStyles(themeStyles, classes),
    );

    const buttonClassName = classNames(
      defaultStyles.root,
      styles[type],
      styles[size],
      {
        [styles.disabled]: disabled,
        [styles.loading]: isLoading,
        [styles.focused]: isFocused,
        [styles.rounded]: rounded,
      },
      className,
    );

    return (
      <button
        ref={ref}
        type="button"
        aria-label={ariaLabel}
        className={buttonClassName}
        onMouseUp={onMouseUp}
        onClick={handleClick}
        disabled={disabled || isLoading}
        data-test-id={dataTestId}
      >
        {isLoading && (
          <div className={defaultStyles.loader}>
            <Loader
              color="currentColor"
              size={16}
              strokeWidth={2}
              padding={0}
              {...loaderProps}
            />
          </div>
        )}
        <div
          className={classNames(defaultStyles.icon, {
            [defaultStyles.iconHidden]: isLoading,
          })}
        >
          {svgIcon || (
            <MaterialIcon
              icon={icon || ''}
              size={`md-${iconSizeValue}`}
              symbolsOutlined={symbolsOutlined}
              filled={filled}
            />
          )}
        </div>
        {counter !== undefined && counter > 0 && (
          <div className={classNames(defaultStyles.counter, styles.counter)}>
            {counter > 9 ? '9+' : counter}
          </div>
        )}
      </button>
    );
  },
);

export default memo(IconButton);
